class LsPriceRange extends HTMLElement {
  connectedCallback() {
    this.currency = this.dataset.currency || 'EUR';
    this.locale = document.documentElement.lang || 'it-IT';
    this.fractionDigits = new Intl.NumberFormat(this.locale, {
      style: 'currency',
      currency: this.currency,
    }).resolvedOptions().maximumFractionDigits;

    this.addEventListener('input', this.handleRangeInput);
    this.addEventListener('change', this.handleRangeChange);
    this.handleResultsUpdate = () => requestAnimationFrame(() => requestAnimationFrame(() => this.refreshElements()));
    document.addEventListener('shopify:collection:update', this.handleResultsUpdate);
    document.addEventListener('shopify:search:update', this.handleResultsUpdate);

    this.mutationObserver = new MutationObserver(() => {
      cancelAnimationFrame(this.refreshFrame);
      this.refreshFrame = requestAnimationFrame(() => this.refreshElements());
    });
    this.mutationObserver.observe(this, { childList: true, subtree: true });

    this.refreshElements();
  }

  disconnectedCallback() {
    this.removeEventListener('input', this.handleRangeInput);
    this.removeEventListener('change', this.handleRangeChange);
    document.removeEventListener('shopify:collection:update', this.handleResultsUpdate);
    document.removeEventListener('shopify:search:update', this.handleResultsUpdate);
    this.mutationObserver?.disconnect();
    cancelAnimationFrame(this.refreshFrame);
  }

  handleRangeInput = (event) => {
    if (event.target === this.minRange) this.update('min', false);
    if (event.target === this.maxRange) this.update('max', false);
  };

  handleRangeChange = (event) => {
    if (event.target !== this.minRange && event.target !== this.maxRange) return;

    event.stopPropagation();
    this.update(event.target === this.minRange ? 'min' : 'max', true);
  };

  refreshElements() {
    this.minRange = this.querySelector('[data-ls-range-min]');
    this.maxRange = this.querySelector('[data-ls-range-max]');
    this.minLabel = this.querySelector('[data-ls-range-min-label]');
    this.maxLabel = this.querySelector('[data-ls-range-max-label]');
    this.priceFacet = this.closest('price-facet-component');
    this.minInput = this.priceFacet?.querySelector('[ref="minInput"]');
    this.maxInput = this.priceFacet?.querySelector('[ref="maxInput"]');

    if (!(this.minRange instanceof HTMLInputElement) || !(this.maxRange instanceof HTMLInputElement)) return;

    const minimumLimit = this.querySelector('[data-ls-range-limit-min]');
    const maximumLimit = this.querySelector('[data-ls-range-limit-max]');
    const formattedMinimum = this.formatMoney(0);
    const formattedMaximum = this.formatMoney(Number(this.dataset.max));
    if (minimumLimit && minimumLimit.textContent !== formattedMinimum) minimumLimit.textContent = formattedMinimum;
    if (maximumLimit && maximumLimit.textContent !== formattedMaximum) maximumLimit.textContent = formattedMaximum;
    this.render();
  }

  update(source, commit) {
    const minimumGap = 1;
    let minValue = Number(this.minRange.value);
    let maxValue = Number(this.maxRange.value);

    if (source === 'min' && minValue > maxValue - minimumGap) {
      minValue = maxValue - minimumGap;
      this.minRange.value = String(minValue);
    }

    if (source === 'max' && maxValue < minValue + minimumGap) {
      maxValue = minValue + minimumGap;
      this.maxRange.value = String(maxValue);
    }

    this.syncTextInputs(minValue, maxValue);
    this.render();

    if (commit) {
      const target = source === 'min' ? this.minInput : this.maxInput;
      target?.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  syncTextInputs(minValue, maxValue) {
    if (this.minInput instanceof HTMLInputElement) {
      this.minInput.value = minValue === 0 ? '' : this.toInputValue(minValue);
    }

    if (this.maxInput instanceof HTMLInputElement) {
      const maximum = Number(this.dataset.max);
      this.maxInput.value = maxValue === maximum ? '' : this.toInputValue(maxValue);
    }
  }

  syncFromTextInputs() {
    const maximum = Number(this.dataset.max);
    const minValue = this.fromInputValue(this.minInput?.value, 0);
    const maxValue = this.fromInputValue(this.maxInput?.value, maximum);

    this.minRange.value = String(Math.min(minValue, maxValue));
    this.maxRange.value = String(Math.max(minValue, maxValue));
    this.render();
  }

  render() {
    if (!(this.minRange instanceof HTMLInputElement) || !(this.maxRange instanceof HTMLInputElement)) return;
    if (!(this.minLabel instanceof HTMLOutputElement) || !(this.maxLabel instanceof HTMLOutputElement)) return;

    const maximum = Number(this.dataset.max) || 1;
    const minValue = Number(this.minRange.value);
    const maxValue = Number(this.maxRange.value);
    const minPercent = (minValue / maximum) * 100;
    const maxPercent = (maxValue / maximum) * 100;

    this.style.setProperty('--ls-range-min', `${minPercent}%`);
    this.style.setProperty('--ls-range-max', `${maxPercent}%`);
    const formattedMinimum = this.formatMoney(minValue);
    const formattedMaximum = this.formatMoney(maxValue);
    if (this.minLabel.textContent !== formattedMinimum) this.minLabel.textContent = formattedMinimum;
    if (this.maxLabel.textContent !== formattedMaximum) this.maxLabel.textContent = formattedMaximum;
    this.minLabel.style.transform = `translateX(${minPercent < 10 ? 0 : -50}%)`;
    this.maxLabel.style.transform = `translateX(${maxPercent > 90 ? -100 : -50}%)`;
    this.minLabel.style.setProperty('--ls-label-pointer', minPercent < 10 ? '8px' : '50%');
    this.maxLabel.style.setProperty('--ls-label-pointer', maxPercent > 90 ? 'calc(100% - 8px)' : '50%');
  }

  formatMoney(minorUnits) {
    const divisor = 10 ** this.fractionDigits;
    return new Intl.NumberFormat(this.locale, {
      style: 'currency',
      currency: this.currency,
      maximumFractionDigits: this.fractionDigits,
    }).format(minorUnits / divisor);
  }

  toInputValue(minorUnits) {
    const divisor = 10 ** this.fractionDigits;
    return (minorUnits / divisor).toLocaleString(this.locale, {
      useGrouping: false,
      minimumFractionDigits: this.fractionDigits,
      maximumFractionDigits: this.fractionDigits,
    });
  }

  fromInputValue(value, fallback) {
    if (!value) return fallback;

    const normalized = value.replace(/\s/g, '').replace(',', '.').replace(/[^\d.-]/g, '');
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? Math.round(parsed * 10 ** this.fractionDigits) : fallback;
  }
}

if (!customElements.get('ls-price-range')) {
  customElements.define('ls-price-range', LsPriceRange);
}
